import React, {type PropsWithChildren, useState} from 'react';
import {
  FlatList,
  Image,
  type ImageProps,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import type {NavigationProp} from '@react-navigation/native';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import {LockLightIcon} from 'src/components/img/svg/rewards';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import AccountStore from 'src/stores/AccountStore';
import AVText from 'src/components/elements/AVText';
import {getDescriber} from 'src/components/screens/descriptor/DescriptorType';
import {IsConnectedProps} from 'src/types/Screen';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import withIsConnected from 'src/components/hoc/withIsConnected';
import {RewardTier} from 'src/types/RewardsModel';
import {
  RewardCashType,
  RewardItemType,
  RewardProductType,
} from 'src/types/Rewards';
import CashRewardTile from 'src/components/elements/rewards/CashRewardTile';
import ProductRewardTile from 'src/components/elements/rewards/ProductRewardTile';
import {HorizontalScrollWrapper} from '../../utils/HorizontalScrollWrapper';

interface RedeemPoints4RewardsProps extends IsConnectedProps {
  navigation: NavigationProp<Record<string, never>>;
}

const lineHeightMultiplier = 1.14;
const RedeemPoints4Rewards = (_props: RedeemPoints4RewardsProps) => {
  const {rewardProducts} = AccountStore.getRewardProducts() || {};

  const handleViewClick = async (item, point) => {
    if (_props.isConnected) {
      NavActions.navigate(AppRoutes.RedeemRewardPanel, {
        productName: item.name,
        imageUrl: item.imageUrl,
        upc: item.upc,
        point,
        rewardProductId: item.rewardProductId,
        productId: item.productId,
      });
    } else {
      NavActions.navigate(AppRoutes.ServerErrorDialog, {
        errorTitle: Localized.Labels.redeem_rewd,
        errorDesc: Localized.Errors.error_rewards_modal,
        errorBtnName: getDescriber().getGoToSettings(),
      });
      return;
    }
  };

  const handleCashRewardClick = async (item, point) => {
    NavActions.navigate(AppRoutes.RedeemCashReward, {
      cashReward: item,
      pointsRequired: point,
    });
  };

  const getRewards = (tier: RewardTier) => {
    let rewards: Array<RewardProductType | RewardCashType> = tier.products;
    if (
      tier.rewardCash &&
      !rewards.some((item) => item.type === RewardItemType.CASH)
    ) {
      rewards.push(tier.rewardCash);
    }
    return rewards;
  };

  const renderRewardItem = (
    item: RewardProductType | RewardCashType,
    tier: RewardTier,
  ) => {
    let product;
    let cash;
    item.type === RewardItemType.PRODUCT ? (product = item) : (cash = item);
    if (product && product.type === RewardItemType.PRODUCT) {
      return (
        <ProductRewardTile
          tier={tier}
          rewardItem={product}
          onRewardPress={handleViewClick}
        />
      );
    } else if (cash && cash.type === RewardItemType.CASH) {
      return (
        <CashRewardTile
          rewardItem={cash}
          tier={tier}
          onRewardPress={handleCashRewardClick}
        />
      );
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.titleHead}>
        <AVText
          style={[
            styles.redeemPoints,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
          accessibilityLabel={Localized.Labels.redeem_points_for_rewards}
        >
          {Localized.Labels.redeem_points_for_rewards}
        </AVText>

        <AVText
          style={[
            styles.redeemAvailablity,
            getSnackDescriber()?.snackDetailsBtnFontSTyle(),
          ]}
          accessibilityLabel={Localized.Labels.redeem_rewards_availability}
        >
          {Localized.Labels.redeem_rewards_availability}
        </AVText>
      </View>

      {rewardProducts?.map((tier) => (
        <View key={tier.tierLevel} style={styles.tierContainer}>
          <View style={styles.productTierPoints}>
            {tier.isLocked && <LockLightIcon />}

            <AVText
              style={[
                styles.pointsReq,
                getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              ]}
            >
              {Localized.Labels.formatString(
                Localized.Labels.points_number,
                tier.pointsRequired,
              )}
            </AVText>
          </View>
          <HorizontalScrollWrapper>
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.productListContainer}
              data={getRewards(tier)}
              renderItem={({item}) => renderRewardItem(item, tier)}
            />
          </HorizontalScrollWrapper>
        </View>
      ))}
    </View>
  );
};

export const ImageWithPlaceholder = ({
  source,
  width,
  height,
  children,
}: PropsWithChildren<
  Pick<ImageProps, 'source'> & {height?: number; width?: number}
>) => {
  const [display, setDisplay] = useState<null | 'none' | 'flex'>(null);

  return (
    <View
      style={{
        width,
        height,
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      {(!display || display === 'none') && children}

      <Image
        source={source}
        onLoad={(evt) => {
          // FIXME: android issue - onError is never called
          //  instead onLoad is fired with stub base64 image with 1px height
          if (Platform.OS === 'web' || evt.nativeEvent.source?.height > 1) {
            setDisplay('flex');
          } else {
            setDisplay('none');
          }
        }}
        onError={() => setDisplay('none')}
        style={{display, width, height, position: 'absolute', borderRadius: 8}}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: Styles.Spacing.m3,
    gap: Styles.Spacing.m3,
  },
  titleHead: {
    paddingHorizontal: Styles.Spacing.m3,
    gap: Styles.Spacing.m2,
  },
  tierContainer: {
    gap: Styles.Spacing.m3,
    marginBottom: Styles.Spacing.m1,
  },
  productTierPoints: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: Styles.Spacing.m3,
  },
  productListContainer: {
    gap: Styles.Spacing.m2,
    paddingHorizontal: Styles.Spacing.m3,
    paddingBottom: 4, // to avoid shadow clipping
  },
  pointsReq: {
    fontSize: Styles.Fonts.f1 + 4,
    fontWeight: '700',
    lineHeight: (Styles.Fonts.f1 + 2) * lineHeightMultiplier,
  },
  redeemAvailablity: {
    fontSize: Styles.Fonts.f1 - 2,
    fontStyle: 'italic',
    lineHeight: (Styles.Fonts.f1 - 2) * lineHeightMultiplier,
  },
  redeemPoints: {
    fontSize: Styles.Fonts.f1 + 2,
    fontWeight: '700',
    lineHeight: (Styles.Fonts.f1 + 2) * lineHeightMultiplier,
    textTransform: 'capitalize',
  },
});

export default withIsConnected(RedeemPoints4Rewards);
